import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import I from 'immutable';
import moment from 'moment';
import React, {CSSProperties, MouseEvent} from 'react';

import hiddenThumbnailSrc from 'app/assets/images/hidden-thumbnail.png';
import TilerThumbnail from 'app/components/TilerThumbnail';
import {CartItemV1, isPartialAoiItem, useRemoveWithUndo} from 'app/modules/Cart';
import {ApiFeature, ApiFeatureData, ApiOrderableScene} from 'app/modules/Remote/Feature/types';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import COLORS from 'app/styles/colors.json';
import * as C from 'app/utils/constants';
import * as featureUtils from 'app/utils/featureUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import {getLayer} from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';

import cs from './styles.module.scss';

export type Props = {
  organization: I.ImmutableOf<ApiOrganization>;
  layerKey: string;

  className?: string;
  style?: CSSProperties;

  isOrdered: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  renderRightElement?: () => null | React.ReactElement;

  blockedSources?: string[];

  showThumbnail?: boolean;
  thumbnailOverrideUrl?: string | undefined;
  /** Drawn over the thumbnail. Absolutely positioned, so it costs the row no width or height. */
  thumbnailBadge?: React.ReactNode;
  /** Drawn across the thumbnail, and only while the row is hovered or focused. */
  thumbnailOverlay?: React.ReactNode;
} & (
  | {
      orderableScene: I.ImmutableOf<ApiOrderableScene>;
      featureDatum?: undefined;
    }
  | {
      featureDatum: I.ImmutableOf<ApiFeatureData>;
      orderableScene?: undefined;
    }
);

const processOrderableScene = (
  orderableScene: I.ImmutableOf<ApiOrderableScene>
): {
  sceneSensingTime: string;
  sourceDetails: any;
  sourceId: string;
  sourceUrl: string | undefined;
} => {
  const sceneSensingTime = orderableScene.get('sensingTime');
  const details = featureUtils.imageAndSourceDetailsFromScene(orderableScene);

  return {
    sceneSensingTime,
    sourceDetails: details.sourceDetails,
    sourceId: details.sourceId,
    sourceUrl: details.url,
  };
};

const processFeatureDatum = (
  featureDatum: I.ImmutableOf<ApiFeatureData>,
  layerKey: string,
  organization: I.ImmutableOf<ApiOrganization>
): {
  sceneSensingTime: string;
  sourceDetails: any;
  sourceId: string;
  sourceUrl: string | undefined;
} => {
  const sceneSensingTime = featureDatum.get('date');
  const details = featureUtils.imageAndSourceDetails(featureDatum, layerKey, organization);

  return {
    sceneSensingTime,
    sourceDetails: details.sourceDetails,
    sourceId: details.sourceId,
    sourceUrl: details.url,
  };
};

// A date list can hold hundreds of cards, and each tiler thumbnail costs the
// tiler a COG read that competes with the map's own tiles. So a card fetches
// its thumbnail only once it comes within this margin of its list's visible area.
const THUMBNAIL_FETCH_MARGIN = '300px';

function findScrollParent(el: HTMLElement): HTMLElement | null {
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const {overflowY} = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
  }
  return null;
}

// Latches true once the element has come near its scroll container's visible
// area, so a thumbnail stays loaded after its card scrolls back out of view.
function useHasBeenNearView(ref: React.RefObject<HTMLElement | null>): boolean {
  const [hasBeenNearView, setHasBeenNearView] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (hasBeenNearView || !el) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setHasBeenNearView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenNearView(true);
        }
      },
      // rootMargin only grows the root, so the root has to be the list's own
      // scroll container for cards just past its edge to count as near.
      {root: findScrollParent(el), rootMargin: THUMBNAIL_FETCH_MARGIN}
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, hasBeenNearView]);

  return hasBeenNearView;
}

const DateSelectCard: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  layerKey,
  featureDatum,
  orderableScene,
  organization,
  className,
  style,
  onClick,
  renderRightElement,
  isOrdered,
  blockedSources = C.THUMBNAIL_BLOCKED_SOURCES,
  showThumbnail = true,
  thumbnailOverrideUrl = undefined,
  thumbnailBadge,
  thumbnailOverlay,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hasBeenNearView = useHasBeenNearView(containerRef);
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);
  // Tracks whether the thumbnail actually rendered (vs. a fetch failure or 404),
  // so we only offer the hover preview when there's a valid image to enlarge.
  const [hasValidPreview, setHasValidPreview] = React.useState(false);

  const handleThumbnailLoad = () => {
    setIsImageLoaded(true);
    setHasValidPreview(true);
  };
  const handleThumbnailError = () => {
    setIsImageLoaded(true);
    setHasValidPreview(false);
  };

  // Get scene and source data based on input type
  const {sceneSensingTime, sourceDetails, sourceId, sourceUrl} = orderableScene
    ? processOrderableScene(orderableScene)
    : processFeatureDatum(featureDatum, layerKey, organization);

  const {operator, name, resolution} = sourceDetails;

  const isBlocked = !isOrdered && blockedSources.includes(sourceId);

  // Tiler thumbnails are fetched with header-based auth (via TilerThumbnail) so
  // their URLs stay free of per-user query params and share a Cloudflare cache
  // key. Blocked sources and override thumbnails use a plain <img> below.
  const tilerThumbnailUrl =
    !isBlocked && !thumbnailOverrideUrl && sourceUrl && mapUtils.isTilerUrl(sourceUrl)
      ? sourceUrl
      : undefined;

  // for multispectral imagery previews, we are currently just swapping in a grayscaled
  // version of the corresponding pan-sharpened version of the image.
  const staticThumbnailSrc = isBlocked ? hiddenThumbnailSrc : (thumbnailOverrideUrl ?? sourceUrl);

  const imageContainerClassName = classnames(cs.imageContainer, {
    [cs.animatedBackground]: !isImageLoaded,
  });

  const thumbnailEl = tilerThumbnailUrl ? (
    <TilerThumbnail
      className={cs.image}
      url={tilerThumbnailUrl}
      enabled={hasBeenNearView}
      onLoad={handleThumbnailLoad}
      onError={handleThumbnailError}
    />
  ) : (
    <img
      className={classnames(cs.image, {
        [cs.grayscale]: !!thumbnailOverrideUrl,
      })}
      loading="lazy"
      src={staticThumbnailSrc}
      onLoad={handleThumbnailLoad}
      onError={handleThumbnailError}
    />
  );

  // Hovering the thumbnail opens a larger preview of the same image, but only
  // once that image has actually loaded. Blocked sources just show a placeholder
  // graphic, so there's nothing useful to enlarge.
  const canZoomThumbnail = !isBlocked && hasValidPreview;

  // Popover takes a single child as its target, so a badge has to share a wrapper with the image
  // rather than sit beside it. Only wrap when there's a badge, so rows without one keep the DOM
  // they've always had.
  const thumbnailTarget =
    thumbnailBadge || thumbnailOverlay ? (
      <div className={cs.thumbnailStack}>
        {thumbnailEl}
        {thumbnailBadge}
        {thumbnailOverlay && <div className={cs.thumbnailOverlay}>{thumbnailOverlay}</div>}
      </div>
    ) : (
      thumbnailEl
    );

  return (
    <div
      ref={containerRef}
      className={cs.container}
      onClick={(event: MouseEvent<HTMLElement>) => {
        if (onClick) {
          onClick(event);
        }
      }}
      data-date={sceneSensingTime} // Target of scrollToSelectedCursor in DateSelect
    >
      <div
        className={classnames(cs.card, {[cs.cardHover]: onClick}, className)}
        style={{height: 72, padding: 6, margin: '-6px -6px 6px -6px', ...style}}
      >
        <div className={cs.datumDetails}>
          {showThumbnail && (
            // Portal-rendered (default) so the larger preview isn't clipped by
            // the card's `overflow: hidden`. Disabled until a valid image has
            // loaded so we never open an empty preview.
            <B.Popover
              // hover-target (not plain hover) so the preview closes the moment
              // the cursor leaves the thumbnail, even if it's over the enlarged image.
              interactionKind="hover-target"
              minimal
              position={B.Position.RIGHT}
              hoverOpenDelay={150}
              disabled={!canZoomThumbnail}
              targetTagName="div"
              targetProps={{className: imageContainerClassName}}
              content={
                <div className={cs.zoomPopover}>
                  {tilerThumbnailUrl ? (
                    <TilerThumbnail className={cs.zoomImage} url={tilerThumbnailUrl} />
                  ) : (
                    <img
                      className={classnames(cs.zoomImage, {
                        [cs.grayscale]: !!thumbnailOverrideUrl,
                      })}
                      src={staticThumbnailSrc}
                    />
                  )}
                </div>
              }
            >
              {thumbnailTarget}
            </B.Popover>
          )}
          <div>
            <div className={cs.dateRow}>
              <div className={cs.date}>{imageryUtils.formatDate(sceneSensingTime, layerKey)}</div>
              {featureDatum && (
                <DateIndicator
                  sceneSensingTime={sceneSensingTime}
                  layerKey={layerKey}
                  featureDatum={featureDatum}
                />
              )}
            </div>
            {operator && (
              <div className={cs.source}>
                {operator} {name}
                {resolution && (
                  <>
                    {' '}
                    (<span className={cs.sourceResolution}>{parseFloat(resolution.toFixed(3))}m</span>)
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {renderRightElement && <div>{renderRightElement()}</div>}
      </div>
    </div>
  );
};

export default DateSelectCard;

export const HiddenImageAction: React.FunctionComponent<
  React.PropsWithChildren<{
    feature: I.MapAsRecord<I.ImmutableFields<ApiFeature>>;
    orderableScene: I.MapAsRecord<I.ImmutableFields<ApiOrderableScene>>;
    isHidden: boolean;
    setIsImageHidden: (
      f: I.ImmutableOf<ApiFeature>,
      orderableScene: I.ImmutableOf<ApiOrderableScene>,
      isHidden: boolean
    ) => void;
    alwaysVisible?: boolean;
  }>
> = ({feature, orderableScene, isHidden, setIsImageHidden, alwaysVisible}) => {
  // We specifically say "unhide" instead of "show" to avoid other confusion
  // around what "showing" an image might be.
  const label = isHidden ? 'Unhide this image' : 'Hide this image (you can unhide it later)';

  return (
    <B.Tooltip content={label}>
      <B.AnchorButton
        icon={isHidden ? 'eye-open' : 'eye-off'}
        aria-label={label}
        color={COLORS.lightSlate}
        minimal
        className={classnames(cs.rightCardAction, {
          [cs.visibleOnHover]: !alwaysVisible,
        })}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setIsImageHidden(feature, orderableScene, !isHidden);
        }}
      />
    </B.Tooltip>
  );
};

/**
 * Takes a scene back out of the cart from its row. Occupies the same rail slot as
 * HiddenImageAction rather than sitting beside it: `visibleOnHover` only hides the button, it
 * still claims its width, and the rail has no room for both at the sidebar's narrowest.
 */
export const RemoveFromCartAction: React.FunctionComponent<{
  item: CartItemV1;
  alwaysVisible?: boolean;
}> = ({item, alwaysVisible}) => {
  const removeWithUndo = useRemoveWithUndo();

  // A hand-drawn area exists only in the cart item, so re-adding the scene won't bring it back —
  // the label says what is actually at stake before the click.
  const label = isPartialAoiItem(item)
    ? 'Remove this partial area from your cart'
    : 'Remove this scene from your cart';

  return (
    <B.Tooltip content={label}>
      <B.AnchorButton
        icon="trash"
        aria-label={label}
        color={COLORS.lightSlate}
        minimal
        className={classnames(cs.rightCardAction, {
          [cs.visibleOnHover]: !alwaysVisible,
        })}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          removeWithUndo(item);
        }}
      />
    </B.Tooltip>
  );
};

/* There are two cases where we want to show the date indicator:
 * 1. The layer is set to be a mosaic layer.
 * 2. The featureDatum has multiple sensing dates. This means the scene is a mosaic
 *    with a specific date range and we want to show it.
 */
const DateIndicator: React.FC<{
  sceneSensingTime: string;
  layerKey: string;
  featureDatum: I.ImmutableOf<ApiFeatureData>;
}> = ({sceneSensingTime, layerKey, featureDatum}) => {
  const layerIsMosaic = !!getLayer(layerKey)?.mosaic;

  // Check to see if we have multiple sensing dates on the featureDatum.
  const sensingDates = featureUtils.getFeatureDataSensingDates(featureDatum, layerKey);
  const earliestDate = moment(sensingDates[0]);
  const latestDate = moment(sensingDates[sensingDates.length - 1]);
  const sceneIsMosaic =
    !earliestDate.isSame(sceneSensingTime, 'day') || !latestDate.isSame(sceneSensingTime, 'day');

  if (!layerIsMosaic && !sceneIsMosaic) return null;

  return (
    <B.Tooltip
      content={imageryUtils.formatCaptureDate(sceneSensingTime, layerKey, {featureDatum})}
      position={B.Position.RIGHT}
      modifiers={{preventOverflow: {enabled: false}, hide: {enabled: false}}}
    >
      <B.Icon icon="calendar" size={10} className={cs.dateIcon} />
    </B.Tooltip>
  );
};
